import React, { useEffect } from 'react';

const VideoModal = ({ isOpen, onClose, video, videos = [], currentIndex = 0, onVideoChange }) => {
  // Log component props on mount and when they change
  useEffect(() => {
    console.log('[VideoModal] Component mounted or props changed:', {
      isOpen,
      hasVideo: !!video,
      videoId: video?.id,
      videoTitle: video?.title,
      videosLength: videos?.length,
      currentIndex,
      hasOnClose: !!onClose,
      hasOnVideoChange: !!onVideoChange
    });
    
    return () => {
      console.log('[VideoModal] Component unmounting');
    };
  }, [isOpen, video, videos, currentIndex, onClose, onVideoChange]);

  // Log when the modal should be visible but isn't rendering
  useEffect(() => {
    if (isOpen && !video) {
      console.error('[VideoModal] Modal isOpen is true but video is missing or invalid:', video);
    }
  }, [isOpen, video]);

  if (!isOpen || !video) {
    console.log(`[VideoModal] Not rendering because isOpen: ${isOpen}, hasVideo: ${!!video}`);
    return null;
  }
  
  console.log('[VideoModal] Rendering modal with video:', { id: video.id, title: video.title });

  const handleOverlayClick = (e) => {
    console.log('[VideoModal] Overlay clicked');
    if (e.target === e.currentTarget) {
      console.log('[VideoModal] Calling onClose');
      onClose();
    } else {
      console.log('[VideoModal] Click was inside modal, not closing');
    }
  };

  const handlePrevVideo = (e) => {
    console.log('[VideoModal] Previous button clicked');
    e.stopPropagation();
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      console.log(`[VideoModal] Changing to previous video, new index: ${newIndex}`);
      onVideoChange(newIndex);
    } else {
      console.log('[VideoModal] Already at first video');
    }
  };

  const handleNextVideo = (e) => {
    console.log('[VideoModal] Next button clicked');
    e.stopPropagation();
    if (currentIndex < videos.length - 1) {
      const newIndex = currentIndex + 1;
      console.log(`[VideoModal] Changing to next video, new index: ${newIndex}`);
      onVideoChange(newIndex);
    } else {
      console.log('[VideoModal] Already at last video');
    }
  };

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < videos.length - 1;

  // Calculate dynamic height based on viewport
  const [videoHeight, setVideoHeight] = React.useState(0);

  React.useEffect(() => {
    const calculateVideoHeight = () => {
      // Base height is 70% of viewport, but ensure we have enough space for controls
      const viewportHeight = window.innerHeight * 0.9; // Account for 5% padding top and bottom
      const minVideoHeight = 300; // Minimum video height in pixels
      const maxVideoHeight = viewportHeight * 0.8; // Max 80% of viewport
      
      // Calculate based on 16:9 aspect ratio
      const viewportWidth = window.innerWidth * 0.9; // Account for 5% padding left and right
      const widthBasedHeight = (viewportWidth * 9) / 16;
      
      // Use the smaller of the two heights to ensure everything fits
      let height = Math.min(widthBasedHeight, maxVideoHeight);
      
      // Ensure we don't go below minimum height
      return Math.max(height, minVideoHeight);
    };

    const handleResize = () => {
      setVideoHeight(calculateVideoHeight());
    };

    // Set initial height
    handleResize();
    
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Navigation Button Component
  const NavButton = ({ direction, onClick, disabled }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'none',
        border: 'none',
        color: disabled ? '#666' : '#fff',
        fontSize: '2.5em',
        cursor: disabled ? 'default' : 'pointer',
        padding: '0 15px',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
      onMouseOver={(e) => !disabled && (e.currentTarget.style.transform = 'scale(1.2)')}
      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
      aria-label={`${direction} video`}
    >
      {direction === 'Previous' ? '❮' : '❯'}
    </button>
  );

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        padding: '20px',
        boxSizing: 'border-box',
        overflow: 'auto',
      }}
      onClick={handleOverlayClick}
    >
      <div 
        style={{
          width: '100%',
          maxWidth: '90vw',
          maxHeight: '90vh',
          backgroundColor: '#000',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          margin: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Video Container */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: `${videoHeight}px`,
          maxHeight: '80vh',
          overflow: 'hidden',
          backgroundColor: '#000',
          flexShrink: 0
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <iframe
              src={`https://player.vimeo.com/video/${video.vimeo_id}?autoplay=1&color=ffffff&title=0&byline=0&portrait=0`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={video.title || 'Video Player'}
            />
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
              zIndex: 1000,
              padding: 0,
              margin: 0,
              lineHeight: 1,
              boxSizing: 'border-box',
              outline: 'none',
              transition: 'transform 0.2s, background 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            aria-label="Close video"
          >
            &times;
          </button>
        </div>

        {/* Video Info */}
        <div style={{ 
          padding: '10px 15px', 
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          flex: '0 0 auto',
          boxSizing: 'border-box',
          width: '100%',
          '@media (max-height: 600px)': {
            padding: '5px 10px'
          }
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            gap: '10px',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            overflow: 'hidden',
            '@media (max-width: 768px)': {
              flexWrap: 'wrap',
              gap: '5px'
            }
          }}>
            <NavButton 
              direction="Previous" 
              onClick={handlePrevVideo} 
              disabled={!hasPrev} 
            />
            
            <div style={{
              flex: '1',
              minWidth: 0,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '5px',
            }}>
              {video.title && (
                <h2 style={{
                  margin: 0,
                  color: '#fff',
                  fontSize: 'min(1.2em, 4vw)',
                  fontFamily: 'CourierPrimeItalic',
                  fontWeight: 'normal',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  lineHeight: '1.2',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  maxWidth: '100%',
                  padding: '0 5px',
                  '@media (max-width: 768px)': {
                    fontSize: 'min(1em, 3.5vw)',
                    textAlign: 'center',
                    padding: 0
                  }
                }}>
                  {video.title}
                </h2>
              )}
              
              {video.client && (
                <p style={{
                  margin: 0,
                  color: '#fff',
                  fontSize: 'min(2.5em, 6vw)',
                  fontFamily: 'BebasNeue',
                  letterSpacing: '1px',
                  lineHeight: '1',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  maxWidth: '100%',
                  '@media (max-width: 768px)': {
                    fontSize: 'min(2em, 5vw)',
                    textAlign: 'center'
                  }
                }}>
                  {video.client}
                </p>
              )}
              
              {(video.director_first_name || video.director_last_name) && (
                <p style={{
                  margin: '2px 0 0 0',
                  color: '#aaa',
                  fontFamily: 'CourierPrime',
                  fontSize: 'min(0.9em, 2.5vw)',
                  fontStyle: 'normal',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  maxWidth: '100%',
                  padding: '0 5px',
                  '@media (max-width: 768px)': {
                    fontSize: 'min(0.8em, 2vw)',
                    textAlign: 'center',
                    marginTop: '2px',
                    padding: 0
                  }
                }}>
                  Directed by {video.director_first_name} {video.director_last_name}
                </p>
              )}
            </div>
            
            <NavButton 
              direction="Next" 
              onClick={handleNextVideo} 
              disabled={!hasNext} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
